import { ExternalAppId } from "../generated/enums"
import { UserPermissions } from "../permissions/types"
import { IOmniaSession, LocaleString } from "../types"
import { RoleId } from "../user"
import { AppConnectionMode, IAppDefinition } from "./types"

/**
 * Returns a list of all supported connected applications
 *
 * It must be noted that while iSoyl has an id in ExternalAppId,
 * it is however not included in the list of supported apps,
 * because it only acts as a wrapper for Dropbox
 *
 * @param session Session
 * @returns Supported apps
 */
export function getSupportedApps(session: IOmniaSession): IAppDefinition[] {

    return [
        getDropbox(),
        getJohnDeere(session),
        getClaas(session),
        getFieldView(session),
        getGlgm()
    ].filter(i => !!i)

}

export function getSupportedApp(session: IOmniaSession, appId: ExternalAppId): IAppDefinition {

    switch (appId) {
        case ExternalAppId.Dropbox: return getDropbox()
        case ExternalAppId.JohnDeere: return getJohnDeere(session)
        case ExternalAppId.Claas: return getClaas(session)
        case ExternalAppId.FieldView: return getFieldView(session)
        case ExternalAppId.Glgm: return getGlgm()
    }

}

function getDropbox(): IAppDefinition {

    const msg = "Allow OmniaHub to access your Dropbox account for importing files into OmniaHub and exporting files to your Dropbox."

    return {

        appId: ExternalAppId.Dropbox,
        name: "Dropbox",
        connectionMode: AppConnectionMode.OAuth,

        logo: "/images/apps/dropbox_glyph_blue-64.png",

        connectPrompt: msg,

        storage: {
            canSelectFolders: true,
            exportsRequireTargetFolder: true
        }

    }

}

function getJohnDeere({ permissions }: { permissions: UserPermissions }): IAppDefinition {

    const msg = "Allow OmniaHub to access your John Deere account for importing files into OmniaHub and exporting files to your John Deere organisation."

    return {

        appId: ExternalAppId.JohnDeere,
        name: "John Deere",
        connectionMode: AppConnectionMode.OAuth,

        logo: "/images/apps/johndeere_icon.png",

        connectPrompt: msg,

        orgs: {
            required: false,
            label: "Default organisation" as LocaleString,
            messages: {

                // Not required for MJD connections
                resetLinks: undefined,

                share: {
                    title: "Target organisation" as LocaleString,
                    prompt: "Please select the target organisation from the list below" as LocaleString,
                    loading: "Loading the available organisations, please wait..." as LocaleString,
                    noMachines: "There are no machines associated with this organisation" as LocaleString
                }

            }
        },

        storage: {
            canSelectFolders: false
        },

        machines: true

    }

}

function getClaas({ permissions }: { permissions: UserPermissions }): IAppDefinition {

    const msg = "Allow OmniaHub to access your CLAAS TELEMATICS account for importing files into OmniaHub."

    return {

        appId: ExternalAppId.Claas,
        name: "CLAAS TELEMATICS",
        connectionMode: AppConnectionMode.OAuth,

        logo: "/images/apps/claas.png",
        icon: "/images/apps/claas_icon.png",

        connectPrompt: msg,

        orgs: {
            required: true,
            label: "Associated farm" as LocaleString,
            messages: {

                // Not required for CLAAS connections
                resetLinks: undefined,

                share: {
                    title: "Target farm" as LocaleString,
                    prompt: "Please select the target farm from the list below" as LocaleString,
                    loading: "Loading the available farms, please wait..." as LocaleString,
                    noMachines: "There are no machines associated with this farm" as LocaleString
                }
            }
        },

        storage: {
            canSelectFolders: false
        },

        machines: !!(permissions?.exports?.claasMachines),

        remoteBoundaries: {
            canUpdate: true,
            canDelete: true
        }

    }

}

function getFieldView({ permissions, currentUserRole }: { permissions: UserPermissions, currentUserRole: RoleId }): IAppDefinition {

    const msg = "Allow OmniaHub to access your Climate FieldView account for importing files into OmniaHub."

    return {

        appId: ExternalAppId.FieldView,
        name: "Climate FieldView",
        connectionMode: AppConnectionMode.OAuth,

        logo: "/images/apps/fieldview.svg",
        icon: "/images/apps/fieldview_icon.png",

        connectPrompt: msg,

        storage: {
            canSelectFolders: false
        },

        machines: false,

        remoteBoundaries: {
            canUpdate: currentUserRole <= RoleId.Admin,
            canDelete: false
        }

    }

}


function getGlgm(): IAppDefinition {

    const msg = "Use this connection to synchronise data from your GLGM account. Use your GLGM account and password to connect."

    return {

        appId: ExternalAppId.Glgm,
        name: "Greenlight Grower Management",
        connectionMode: AppConnectionMode.BasicAuth,

        logo: "/images/apps/glgm_icon.png",

        connectPrompt: msg,

        holdingSync: true,

        orgs: {
            required: true,
            label: "GLGM site",
            messages: {

                resetLinks: {
                    title: "Reset links to the remote site",
                    prompt: "<p>Please note that this will delete all the links between the remote GLGLM site and the current Omnia holding. This operation is irreversible.</p>"
                },

                // Not required for GLGM connections
                share: undefined

            }
        },

    }

}
